"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Board = Board;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const cn_1 = require("../lib/cn");
const color_1 = require("../lib/color");
const misc_1 = require("./misc");
const react_2 = require("react");
const dropdown_menu_1 = require("./dropdown-menu");
const icon_1 = require("./icon");
const tooltip_1 = require("./tooltip");
/**
 * Where the slot goes, given the pointer.
 *
 * ⚠️⚠️ **THE SLOT IS EXCLUDED FROM THE MEASUREMENT AND THAT IS THE WHOLE TRICK.** It is the one
 * element whose position depends on the answer, so including it makes the calculation depend on its
 * own result — which is the flicker item 12 reports. `[data-board-card]` matches only real cards.
 *
 * ⚠️ **The MIDLINE, not the top edge.** Above the middle of a card means *before it*, below means
 * *after it* — which is what the tester asked for in words: *"above a card's midline pushes it
 * down, below pushes it up"*. Measuring against the top edge makes the whole lower half of every
 * card mean *before*, so the gap runs one card ahead of the pointer all the way down the column.
 *
 * ⚠️ **Past every midline is the end**, which is also what the column's own handler answers for the
 * padding below the last card — the two agree rather than fighting over the same region.
 */
function insertionIndex(list, y) {
    const cards = list.querySelectorAll('[data-board-card]');
    for (let at = 0; at < cards.length; at += 1) {
        const rect = cards[at]?.getBoundingClientRect();
        if (rect !== undefined && y < rect.top + rect.height / 2)
            return at;
    }
    return cards.length;
}
function Board({ columns, labels, onMove, onOpen, className, renderBody, renderFooter, }) {
    /**
     * ⚠️ **`dragging` is the card id, and `over` is the column id.** Two pieces of state rather than
     * one object: the drop target changes on every `dragover` and the dragged card does not, so one
     * object would re-render every column on every mouse move.
     */
    const [dragging, setDragging] = (0, react_1.useState)(null);
    /** ⚠️ Item 29 · where in the hovered column the slot opens. `null` is no drag in progress. */
    const [overIndex, setOverIndex] = (0, react_1.useState)(null);
    const [over, setOver] = (0, react_1.useState)(null);
    /**
     * ┌──────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ 17 Oct 2026 · A FINGER PICKS A CARD UP BY HOLDING IT.          │
     * └──────────────────────────────────────────────────────────────────┘
     *
     * HTML5 drag-and-drop never fires on touch, so on a phone a swipe across the board only
     * scrolled it and no card could change column. Now: hold a card for `HOLD_MS` without moving and
     * it lifts — a copy follows the finger, the column under it lights up with its slot, the board
     * scrolls when the finger nears an edge, and lifting the finger drops it. A swipe that MOVES
     * before the hold completes is a scroll, exactly as before.
     *
     * ⚠️ **Touch events, not pointer events.** Only a non-passive `touchmove` can stop the browser
     * from panning once the card is lifted; a pointer listener cannot, and the drag would be
     * cancelled the moment the board began to scroll under it.
     */
    const scroller = (0, react_1.useRef)(null);
    const [lifted, setLifted] = (0, react_1.useState)(null);
    const finger = (0, react_1.useRef)({ x: 0, y: 0 });
    /** A click that ends a drag must not also open the card. */
    const swallowClick = (0, react_1.useRef)(false);
    const targetAt = (x, y) => {
        const element = document.elementFromPoint(x, y);
        const section = element?.closest('[data-board-column]');
        const columnId = section?.dataset.boardColumn;
        if (section === null || section === undefined || columnId === undefined)
            return null;
        const list = section.querySelector('[data-board-list]');
        return { columnId, index: list === null ? 0 : insertionIndex(list, y) };
    };
    (0, react_1.useEffect)(() => {
        if (lifted === null)
            return;
        const card = lifted.card;
        let frame = 0;
        /* The board scrolls while the finger rests near an edge. */
        const tick = () => {
            const box = scroller.current?.getBoundingClientRect();
            if (box !== undefined && scroller.current !== null) {
                const { x } = finger.current;
                if (x < box.left + EDGE)
                    scroller.current.scrollLeft -= 14;
                else if (x > box.right - EDGE)
                    scroller.current.scrollLeft += 14;
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        const onMoveTouch = (event) => {
            event.preventDefault();
            const touch = event.touches[0];
            if (touch === undefined)
                return;
            finger.current = { x: touch.clientX, y: touch.clientY };
            setLifted((current) => current === null ? current : { ...current, x: touch.clientX, y: touch.clientY });
            const target = targetAt(touch.clientX, touch.clientY);
            setOver(target?.columnId ?? null);
            setOverIndex(target?.index ?? null);
        };
        const onEnd = () => {
            const target = targetAt(finger.current.x, finger.current.y);
            setLifted(null);
            swallowClick.current = true;
            window.setTimeout(() => {
                swallowClick.current = false;
            }, 400);
            if (target === null) {
                setDragging(null);
                setOver(null);
                setOverIndex(null);
                return;
            }
            drop(target.columnId, card.id);
        };
        window.addEventListener('touchmove', onMoveTouch, { passive: false });
        window.addEventListener('touchend', onEnd);
        window.addEventListener('touchcancel', onEnd);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('touchmove', onMoveTouch);
            window.removeEventListener('touchend', onEnd);
            window.removeEventListener('touchcancel', onEnd);
        };
        /* ⚠️ Bound once per lift, keyed on the card: re-binding on every finger move would drop the
           listeners mid-gesture. */
    }, [lifted?.card.id]);
    /** A finger on a card: lift it after a still hold, or let it go as a scroll if it moves first. */
    const holdStart = (card, event) => {
        if (onMove === undefined)
            return;
        const touch = event.touches[0];
        if (touch === undefined)
            return;
        const start = { x: touch.clientX, y: touch.clientY };
        finger.current = start;
        const timer = window.setTimeout(() => {
            cleanup();
            setDragging(card.id);
            setLifted({ card, x: finger.current.x, y: finger.current.y });
            /* Widened: `vibrate` is typed as always there and is absent on iOS. */
            navigator.vibrate?.(15);
        }, HOLD_MS);
        const onMoveEarly = (moved) => {
            const now = moved.touches[0];
            if (now === undefined)
                return;
            finger.current = { x: now.clientX, y: now.clientY };
            if (Math.abs(now.clientX - start.x) > SLOP || Math.abs(now.clientY - start.y) > SLOP) {
                cleanup();
            }
        };
        const cleanup = () => {
            window.clearTimeout(timer);
            window.removeEventListener('touchmove', onMoveEarly);
            window.removeEventListener('touchend', cleanup);
            window.removeEventListener('touchcancel', cleanup);
        };
        window.addEventListener('touchmove', onMoveEarly, { passive: true });
        window.addEventListener('touchend', cleanup);
        window.addEventListener('touchcancel', cleanup);
    };
    const drop = (columnId, cardId) => {
        setDragging(null);
        setOver(null);
        /* ⚠️ Cleared here as well as on `dragend` — a successful drop fires both, but a drop outside
           any column fires only the latter, and a slot left open outlives the drag that made it. */
        setOverIndex(null);
        if (cardId === null || onMove === undefined)
            return;
        /* ⚠️ A card dropped on the column it is already in is not a move. Reporting it would fire an
         * undo toast for something that did not happen. */
        const from = columns.find((column) => column.cards.some((card) => card.id === cardId));
        if (from?.id === columnId)
            return;
        onMove(cardId, columnId);
    };
    return ((0, jsx_runtime_1.jsxs)("div", { role: "group", "aria-label": labels.board, ref: scroller, className: (0, cn_1.cn)('min-h-0 flex-1 overflow-auto p-4', className), children: [(0, jsx_runtime_1.jsx)("div", { className: "flex h-full min-w-max gap-3", children: columns.map((column) => {
                    return ((0, jsx_runtime_1.jsxs)("section", { "data-board-column": column.id, "aria-label": labels.column(column.name), className: (0, cn_1.cn)(
                        /* ⚠️⚠️ **`h-full` — item 29's *"column background extends past the cards"*.** A
                           column sized to its content, so one with two cards was a short tile beside one
                           with ten and the board read as a ragged skyline rather than as columns. The
                           background IS the column; it has to be the full height whatever is in it. */
                        'bg-muted/40 flex h-full w-[288px] shrink-0 flex-col rounded-xl border', over === column.id && 'border-primary ring-ring/25 ring-[3px]'), onDragOver: (event) => {
                            /* ⚠️ `preventDefault` is what makes a drop legal at all — without it the browser
                             * refuses the drop and nothing happens, silently. */
                            event.preventDefault();
                            if (over !== column.id)
                                setOver(column.id);
                            /* ⚠️ The header and the column's own padding. The LIST below stops the event when
                               the pointer is among the cards, so reaching here means the pointer is outside
                               them and the slot belongs at the end. */
                            if (overIndex !== column.cards.length)
                                setOverIndex(column.cards.length);
                        }, 
                        /**
                         * ┌───────────────────────────────────────────────────────────────────────────┐
                         * │ ⚠️⚠️ THERE IS NO `dragleave` HANDLER HERE, AND ITS ABSENCE IS THE FIX.     │
                         * └───────────────────────────────────────────────────────────────────────────┘
                         *
                         * Item 12, third pass — and the reporter diagnosed it exactly: *"the preview appears,
                         * but that pushes the card down, so the top half no longer registers, so it
                         * disappears, which puts the card back, so it registers again."*
                         *
                         * ⚠️⚠️ **INSERTING THE SLOT UNDER A STATIONARY POINTER FIRES `dragleave`, AND THAT IS
                         * NOT THE POINTER LEAVING ANYTHING.** The slot opens between two cards, the card below
                         * it shifts down, and the element under the pointer changes without the pointer
                         * moving a pixel — so the browser fires `dragleave` on the card and `dragenter` on the
                         * slot. The handler that used to be here read the first of those as *the drag left
                         * this column*, cleared `over`, and closed the slot; the card sprang back, the next
                         * `dragover` reopened it, and the gap strobed at pointer-move frequency.
                         *
                         * ⚠️⚠️ **`relatedTarget` DOES NOT SAVE IT, WHICH IS WHY THE SECOND PASS FAILED.** The
                         * test *"did focus go somewhere still inside this column"* is the right idea and the
                         * value is unreliable for a leave the DOM caused rather than the pointer: browsers
                         * routinely report `null` there, `contains(null)` is false, and the handler clears
                         * exactly as before. It fixed the card-to-card case and left the slot case standing,
                         * which is why it read as *working half the time* — dropping BELOW a card never opens
                         * a slot under the pointer, and dropping above always does.
                         *
                         * ⚠️⚠️ **SO NOTHING CLEARS ON LEAVE AT ALL.** `dragover` fires continuously wherever
                         * the pointer is, so the column under it sets `over` to itself on every move — moving
                         * to another column re-points it, and a stale highlight cannot outlive the gesture
                         * because `onDragEnd` on the card clears everything and fires on every drag that
                         * ends, cancelled ones included. The only cost is that a drag hovering the gap BETWEEN
                         * two columns leaves the last column highlighted, which is a stale highlight for a few
                         * hundred milliseconds rather than a control fighting the hand using it.
                         *
                         * ⚠️ **A depth counter is the other standard answer and it does not work here either**
                         * — `scoped-drop.tsx` can use one because nothing inside it moves mid-drag. A counter
                         * over a list that inserts an element under the pointer goes out of step the first
                         * time enter and leave do not pair up.
                         */
                        onDrop: (event) => {
                            event.preventDefault();
                            /* The dataTransfer payload first, `dragging` as the fallback: Firefox clears the
                             * former in some drag sequences, and losing the id turns a drop into nothing. */
                            const carried = event.dataTransfer.getData('text/plain');
                            drop(column.id, carried === '' ? dragging : carried);
                        }, children: [(0, jsx_runtime_1.jsxs)("header", { className: "flex items-center gap-2 px-3 py-2.5", children: [(0, jsx_runtime_1.jsx)("span", { className: "size-2 rounded-full", style: { background: column.color }, "aria-hidden": true }), (0, jsx_runtime_1.jsx)("span", { className: "text-[12.5px] font-medium", children: column.name }), (0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground text-[11.5px] tabular-nums", children: column.total ?? column.cards.length })] }), (0, jsx_runtime_1.jsxs)("div", { "data-board-list": "", className: "flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-2 pb-2", 
                                /**
                                 * ┌───────────────────────────────────────────────────────────────────────────┐
                                 * │ ⚠️⚠️ ONE HANDLER FOR THE WHOLE LIST, WHERE EVERY CARD USED TO HAVE ONE.   │
                                 * └───────────────────────────────────────────────────────────────────────────┘
                                 *
                                 * Item 12, 14 Sep — *"a drag insertion gap that does not flicker"*. Two faults,
                                 * one cause:
                                 *
                                 * 1. **Every card claimed the slot ABOVE it**, whichever half of it the pointer
                                 *    was over. So dragging down a column, the gap jumped a whole card ahead of the
                                 *    pointer, and dropping below a card put the card above it.
                                 * 2. **The flicker.** A card's handler fired, the slot opened above it and pushed
                                 *    that card DOWN by the slot's height — which moved the card out from under the
                                 *    pointer, so the next `dragover` hit the card before it, which opened the slot
                                 *    somewhere else, which moved everything back. A feedback loop at pointer-move
                                 *    frequency, which is what a flicker is.
                                 *
                                 * ⚠️⚠️ **THE FIX IS TO MEASURE THE CARDS RATHER THAN TO LISTEN ON THEM.** The
                                 * midlines of the real cards are read at the moment of the move and the slot is
                                 * NOT among them, so opening it cannot change the answer: the gap sits between
                                 * card i-1 and card i, the pointer is inside that gap, and the gap still resolves
                                 * to index i. The loop has nowhere to start.
                                 */
                                onDragOver: (event) => {
                                    /* ⚠️ Stopped, or the SECTION's handler resets the index to the end on the same
                                       event — the two fire in order and the outer one would win. */
                                    event.stopPropagation();
                                    event.preventDefault();
                                    if (over !== column.id)
                                        setOver(column.id);
                                    const next = insertionIndex(event.currentTarget, event.clientY);
                                    if (overIndex !== next)
                                        setOverIndex(next);
                                }, children: [column.cards.map((card, index) => ((0, jsx_runtime_1.jsxs)(react_2.Fragment, { children: [over === column.id && overIndex === index && dragging !== card.id ? ((0, jsx_runtime_1.jsx)("div", { "aria-hidden": true, className: "border-primary/50 bg-primary/5 h-9 shrink-0 rounded-lg border border-dashed" })) : null, (0, jsx_runtime_1.jsx)("div", { "data-board-card": "", children: (0, jsx_runtime_1.jsx)(Card, { card: card, body: renderBody?.(card.id), columns: columns, columnId: column.id, labels: labels, dragging: dragging === card.id, onDragStart: (event) => {
                                                        event.dataTransfer.effectAllowed = 'move';
                                                        event.dataTransfer.setData('text/plain', card.id);
                                                        setDragging(card.id);
                                                    }, onDragEnd: () => {
                                                        setDragging(null);
                                                        setOver(null);
                                                        setOverIndex(null);
                                                    }, onOpen: (id) => {
                                                        if (!swallowClick.current)
                                                            onOpen?.(id);
                                                    }, onMove: onMove, onHold: (event) => {
                                                        holdStart(card, event);
                                                    } }) })] }, card.id))), over === column.id && overIndex === column.cards.length ? ((0, jsx_runtime_1.jsx)("div", { "aria-hidden": true, className: "border-primary/50 bg-primary/5 h-9 shrink-0 rounded-lg border border-dashed" })) : null, column.cards.length === 0 ? (
                                    /* ⚠️ One sentence, and it invites a drop — see `labels.empty` for why the
                                       filter-blaming variant is gone. */
                                    (0, jsx_runtime_1.jsx)("p", { className: "text-muted-foreground rounded-lg border border-dashed px-3 py-5 text-center text-[11.5px] leading-relaxed", children: labels.empty })) : null] }), renderFooter?.(column.id) ?? null] }, column.id));
                }) }), lifted === null ? null : ((0, jsx_runtime_1.jsxs)("div", { "aria-hidden": true, className: "bg-card shadow-modal pointer-events-none fixed z-50 w-[240px] -translate-x-1/2 -translate-y-1/2 rotate-[2deg] rounded-lg border p-2.5", style: { left: lifted.x, top: lifted.y }, children: [(0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground block text-[11px]", children: lifted.card.key }), (0, jsx_runtime_1.jsx)("span", { className: "block text-[12.5px] leading-snug font-medium", children: lifted.card.title })] }))] }));
}
/** How long a finger must rest on a card before it lifts, and how far it may wander meanwhile. */
const HOLD_MS = 350;
const SLOP = 8;
/** How close to the board's edge the finger scrolls it. */
const EDGE = 48;
function Card({ card, body, columns, columnId, labels, dragging, onDragStart, onDragEnd, onOpen, onMove, onHold, }) {
    const elsewhere = columns.filter((column) => column.id !== columnId);
    return ((0, jsx_runtime_1.jsxs)("div", { draggable: true, onDragStart: onDragStart, onDragEnd: onDragEnd, onTouchStart: onHold, 
        /* ⚠️ A held finger must lift the card, not open the phone's own callout or select text. */
        onContextMenu: (event) => {
            if (onHold !== undefined)
                event.preventDefault();
        }, className: (0, cn_1.cn)('bg-card hover:border-primary/40 relative flex cursor-grab flex-col gap-1.5 rounded-lg border p-2.5 shadow-xs transition-colors select-none [-webkit-touch-callout:none]', 
        /* ⚠️⚠️ **LIFTED, NOT FADED — item 29.** `opacity-40` alone made the card look disabled;
           a drag is a card picked UP, so it keeps its colour and gains a shadow and a degree of
           tilt. The slight transparency stays so what is underneath is still readable. */
        dragging && 'shadow-modal rotate-[1.5deg] scale-[1.02] opacity-80'), children: [(0, jsx_runtime_1.jsx)("button", { type: "button", className: "flex flex-col gap-1.5 text-left", onClick: () => {
                    onOpen?.(card.id);
                }, children: body !== undefined ? (body) : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)("span", { className: "flex items-center gap-2", children: [(0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground text-[11px]", children: card.key }), card.chipLabel === undefined ? null : ((0, jsx_runtime_1.jsxs)("span", { className: "text-muted-foreground flex items-center gap-1 text-[10.5px]", children: [(0, jsx_runtime_1.jsx)("span", { "aria-hidden": true, className: "size-2 shrink-0 rounded-full", style: { background: card.chipColor ?? 'currentColor' } }), card.chipLabel] })), (0, jsx_runtime_1.jsx)("span", { className: "flex-1" }), card.initials === undefined ? null : ((0, jsx_runtime_1.jsx)(tooltip_1.Tooltip, { asChild: true, content: card.avatarTitle, children: (0, jsx_runtime_1.jsxs)("span", { className: "relative grid size-5 place-items-center overflow-hidden rounded-full text-[9px] font-semibold", style: { background: card.avatarColor, color: (0, color_1.readableOn)(card.avatarColor) }, children: [(0, jsx_runtime_1.jsx)(misc_1.AvatarFace, { seed: card.avatarSeed, fallback: card.initials, size: 20 }), (0, jsx_runtime_1.jsx)(misc_1.AvatarImage, { src: card.avatarSrc })] }) }))] }), (0, jsx_runtime_1.jsx)("span", { className: "text-[12.5px] leading-snug font-medium", children: card.title }), (0, jsx_runtime_1.jsxs)("span", { className: "text-muted-foreground flex items-center gap-2 text-[11px]", children: [card.dueLabel === undefined ? null : ((0, jsx_runtime_1.jsx)("span", { className: (0, cn_1.cn)('', card.dueLate === true && 'text-destructive'), children: card.dueLabel })), card.estimateLabel === undefined ? null : (0, jsx_runtime_1.jsx)("span", { children: card.estimateLabel })] })] })) }), onMove === undefined || elsewhere.length === 0 ? null : ((0, jsx_runtime_1.jsx)("div", { className: "absolute top-1.5 right-1.5", children: (0, jsx_runtime_1.jsx)(dropdown_menu_1.DropdownMenu, { label: labels.move, items: elsewhere.map((column) => ({
                        label: labels.moveTo(column.name),
                        onSelect: () => {
                            onMove(card.id, column.id);
                        },
                    })), children: (0, jsx_runtime_1.jsx)("button", { type: "button", "aria-label": labels.move, className: "text-muted-foreground hover:text-foreground grid size-8 place-items-center rounded desktop:size-5", children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "ellipsis", size: 13 }) }) }) }))] }));
}
//# sourceMappingURL=board.js.map